import React, { useState, useEffect } from "react";
import {
  PercentIcon, DollarSign, Settings, Plus, Edit,
  Trash2, ArrowUpRight, Users, Star, Clock, Shield,
  Filter, Search, AlertCircle, X, Award, Check,
  Building2, MapPin, Calculator, ChevronDown
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import CommissionDialog from "../../components/ui/CommissionDialog";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { 
  Commission,
  CommissionBackendResponse,
  CommissionStatusEnum,
  CommissionCategoryTypeEnum,
  CommissionTierNameEnum,
  CommissionPayoutMethodEnum,
  CommissionBillingCycleEnum
} from "../../modules/shared/model";
import { 
  fetchCommissions, 
  deleteCommission, 
  clearCommissionError 
} from "../../redux/Slices/commissionSlice";

const CommissionSettings = () => {
  const dispatch = useAppDispatch();
  const { items: commissionRules = [], loading, error, totalCount } = useAppSelector((s: any) => s.commissions);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState<CommissionBackendResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  // Load commissions on component mount
  useEffect(() => {
    dispatch(fetchCommissions(undefined));
  }, [dispatch]);

  // Clear error when component unmounts or error is shown
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearCommissionError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Filter commissions based on search and category
  const filteredCommissions = commissionRules.filter(rule => {
    if (!rule) return false;
    
    // Search filtering
    const matchesSearch = !searchQuery || rule.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Category filtering - backend uses string IDs
    let matchesCategory = true;
    if (selectedCategory !== "all") {
      matchesCategory = rule.categoryType?.id === selectedCategory;
    }
    
    return matchesSearch && matchesCategory;
  });

  // Calculate stats from actual data
  const stats = {
    activeRules: commissionRules.filter(rule => rule?.status?.id === "1").length, // Backend uses string IDs
    avgCommission: commissionRules.length > 0 
      ? Math.round(commissionRules.reduce((sum, rule) => sum + (rule?.baseRate || 0), 0) / commissionRules.length * 10) / 10
      : 0,
    totalCategories: new Set(commissionRules.map(rule => rule?.categoryType?.id).filter(Boolean)).size,
    activeModifiers: commissionRules.reduce((sum, rule) => sum + (rule?.commissionRateModifiers?.length || 0), 0)
  };

  const handleDeleteCommission = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this commission rule?")) {
      try {
        await dispatch(deleteCommission(id)).unwrap();
      } catch (error) {
        console.error("Error deleting commission:", error);
      }
    }
  };

  const getEnumDisplayName = (enumType: any, value: number): string => {
    const enumKey = Object.keys(enumType).find(key => enumType[key] === value);
    return enumKey ? enumKey.replace(/_/g, " ") : "";
  };


  const CommissionCard = ({ rule }: { rule: CommissionBackendResponse }) => {
    if (!rule) {
      return null;
    }

    return (
      <Card className="p-6"   style={{ textAlign: "left" }}>
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">{rule.name || "Unnamed Commission"}</h3>
            <Badge variant={undefined} className={
              rule.status?.id === "1" ? "bg-green-100 text-green-700" :
              "bg-yellow-100 text-yellow-700"
            }>
              {rule.status?.name || (rule.status?.id === "1" ? "Active" : "Inactive")}
            </Badge>
          </div>
          <p className="text-gray-600 mt-1">
            {rule.categoryType?.name || "Unknown Category"}
          </p>
          </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedRule(rule)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600"
            onClick={() => handleDeleteCommission(rule.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Base Rate */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-4">
          <PercentIcon className="h-5 w-5 text-blue-600" />
          <h4 className="font-medium">Base Commission Rate</h4>
        </div>
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="text-2xl font-semibold text-blue-700">{rule.baseRate || 0}%</div>
          <p className="text-sm text-blue-600 mt-1">Standard commission rate</p>
        </div>
      </div>

      {/* Commission Tiers */}
      <div className="mt-6">
        <h4 className="font-medium mb-4">Commission Tiers</h4>
        <div className="space-y-4">
          {(rule.commissionTiers || []).map((tier, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge variant={undefined} className={
                    tier.name?.id === "1" ? "bg-gray-100 text-gray-700" :
                    tier.name?.id === "2" ? "bg-blue-100 text-blue-700" :
                    tier.name?.id === "3" ? "bg-yellow-100 text-yellow-700" :
                    "bg-purple-100 text-purple-700"
                  }>
                    {tier.name?.name || "Unknown"}
                  </Badge>
                  <span className="text-sm text-gray-500">From ${tier.threshold || 0}</span>
                </div>
                <Badge variant={undefined} className="bg-green-100 text-green-700">{tier.rate || 0}%</Badge>
              </div>
              {(tier.conditions || []).length > 0 && (
                <div className="mt-2 space-y-1">
                  {(tier.conditions || []).map((condition, condIndex) => (
                    <div key={condIndex} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600" />
                      {condition}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>


      {/* Rate Modifiers */}
      {(rule.commissionRateModifiers || []).length > 0 && (
        <div className="mt-6">
          <h4 className="font-medium mb-4">Rate Modifiers</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rule.commissionRateModifiers.map((modifier, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <h5 className="font-medium">{modifier.name || "Unnamed Modifier"}</h5>
                  <Badge variant={undefined} className={
                    modifier.adjustment >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }>
                    {modifier.adjustment >= 0 ? "+" : ""}{modifier.adjustment || 0}%
                  </Badge>
                </div>
                {modifier.conditions && modifier.conditions.length > 0 && (
                  <div className="space-y-1">
                    {modifier.conditions.map((condition, condIndex) => (
                      <div key={condIndex} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check className="h-4 w-4 text-green-600" />
                        {condition}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings */}
      <div className="mt-6 pt-6 border-t">
        <h4 className="font-medium mb-4">Payout Settings</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="text-sm">Billing Cycle</div>
            <Badge variant="outline">
              {rule.billingCycle?.name || "Not Set"}
            </Badge>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="text-sm">Minimum Payout</div>
            <Badge variant="outline">${rule.minimumPayout || 0}</Badge>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="text-sm">Payout Method</div>
            <Badge variant="outline">
              {rule.payoutMethod?.name || "Not Set"}
            </Badge>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="text-sm">Auto-Adjustment</div>
            <Badge variant={undefined} className={(rule.autoAdjustment ?? false) ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
              {(rule.autoAdjustment ?? false) ? "Enabled" : "Disabled"}
            </Badge>
          </div>
        </div>
      </div>
    </Card>
  );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col"   style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="text-left">
                <h1 className="text-2xl font-bold text-gray-900">Commission Settings</h1>
                <p className="text-gray-600 mt-1">Manage commission rates and payout rules</p>
              </div>
              <Button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Rule
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Active Rules</p>
                    <p className="text-2xl font-semibold mt-1">{stats.activeRules}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Settings className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Avg Commission</p>
                    <p className="text-2xl font-semibold mt-1">{stats.avgCommission}%</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <PercentIcon className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Total Categories</p>
                    <p className="text-2xl font-semibold mt-1">{stats.totalCategories}</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Building2 className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Rate Modifiers</p>
                    <p className="text-2xl font-semibold mt-1">{stats.activeModifiers}</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Calculator className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 sm:min-w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search commission rules..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>
                
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="all">All Categories</option>
                  <option value="1">Local Services</option>
                  <option value="2">Professional Services</option>
                  <option value="3">International Services</option>
                  <option value="4">Emergency Services</option>
                </select>
              </div>
            </Card>

            {/* Error Display */}
            {error && (
              <Card className="p-4 bg-red-50 border-red-200">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  <p>{error}</p>
                </div>
              </Card>
            )}

            {/* Loading State */}
            {loading && (
              <Card className="p-6">
                <div className="flex items-center justify-center">
                  <p className="text-gray-500">Loading commission rules...</p>
                </div>
              </Card>
            )}

            {/* Commission Rules */}
            {!loading && (
              <div className="grid grid-cols-1 gap-6">
                {filteredCommissions.length === 0 ? (
                  <Card className="p-6">
                    <div className="text-center text-gray-500">
                      <Settings className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium">No commission rules found</p>
                      <p className="text-sm">Create your first commission rule to get started.</p>
                    </div>
                  </Card>
                ) : (
                  filteredCommissions.map(rule => (
                    <CommissionCard key={rule.id} rule={rule} />
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Commission Dialog */}
      <CommissionDialog
        isOpen={showAddModal || !!selectedRule}
        onClose={() => {
          setShowAddModal(false);
          setSelectedRule(null);
          // Refresh commissions list after dialog closes
          dispatch(fetchCommissions(undefined));
        }}
        commission={selectedRule}
      />
    </div>
  );
};

export default CommissionSettings;